import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Bell, BellRing, Megaphone, Check } from "lucide-react";
import { toast } from "sonner";
import { enablePush, isPushEnabled, pushSupported } from "@/lib/api";

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
};

export const AnnouncementsSheet = ({ open, onClose, announcements }) => {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) isPushEnabled().then(setEnabled);
  }, [open]);

  const handleEnable = async () => {
    setLoading(true);
    try {
      await enablePush();
      setEnabled(true);
      toast.success("Notifications enabled", {
        description: "You'll get EYB updates on this device.",
      });
    } catch (e) {
      toast.error("Couldn't enable notifications", { description: e.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="announcements-sheet"
          className="absolute inset-0 z-40 flex flex-col justify-end"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div
            className="absolute inset-0 bg-[#03112a]/70 backdrop-blur-xl"
            onClick={onClose}
            data-testid="announcements-backdrop"
          />

          <motion.div
            className="relative max-h-[82%] overflow-hidden rounded-t-[32px] border-t border-white/15 bg-[#0a2547]/95 backdrop-blur-2xl"
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 260, damping: 30 }}
          >
            <div className="flex items-center justify-between px-6 pb-3 pt-5">
              <div className="flex items-center gap-2">
                <Megaphone size={20} className="text-[#2EC1F0]" />
                <h3 className="font-heading text-lg font-semibold text-white">
                  Announcements
                </h3>
              </div>
              <button
                type="button"
                onClick={onClose}
                data-testid="announcements-close-btn"
                aria-label="Close"
                className="grid h-8 w-8 place-items-center rounded-full bg-white/10 text-white/70 active:scale-90"
              >
                <X size={16} />
              </button>
            </div>

            {/* notifications toggle */}
            {pushSupported() && (
              <div className="mx-6 mb-3 flex items-center justify-between rounded-2xl bg-white/5 px-4 py-3">
                <div className="flex items-center gap-3">
                  {enabled ? (
                    <BellRing size={18} className="text-[#38D6B5]" />
                  ) : (
                    <Bell size={18} className="text-white/70" />
                  )}
                  <span className="text-sm text-white/85">
                    {enabled ? "Notifications on" : "Get push notifications"}
                  </span>
                </div>
                {enabled ? (
                  <span className="flex items-center gap-1 text-xs font-medium text-[#38D6B5]">
                    <Check size={14} /> Enabled
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={handleEnable}
                    disabled={loading}
                    data-testid="enable-push-btn"
                    className="rounded-full bg-gradient-to-r from-[#2EC1F0] to-[#1273C4] px-4 py-1.5 text-xs font-semibold text-white active:scale-95 disabled:opacity-60"
                  >
                    {loading ? "…" : "Enable"}
                  </button>
                )}
              </div>
            )}

            <div className="max-h-[52vh] space-y-3 overflow-y-auto px-6 pb-8">
              {announcements.length === 0 ? (
                <p className="py-10 text-center text-sm text-white/45">
                  No announcements yet.
                </p>
              ) : (
                announcements.map((a) => (
                  <div
                    key={a.id}
                    data-testid="announcement-item"
                    className="rounded-2xl border border-white/10 bg-white/5 p-4"
                  >
                    <div className="mb-1 flex items-center justify-between gap-2">
                      <h4 className="font-semibold text-white">{a.title}</h4>
                      <span className="shrink-0 text-[10px] uppercase tracking-wide text-white/40">
                        {fmt(a.created_at)}
                      </span>
                    </div>
                    <p className="whitespace-pre-wrap text-sm leading-relaxed text-white/70">
                      {a.body}
                    </p>
                  </div>
                ))
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
